import { useState, ReactChild, useEffect, ReactNode } from 'react';

import { Button, IconButton } from '@client:common/button';
import { Ripple } from '@client:common/effects/ripple';
import { FormControl, CheckboxGroup, Checkbox } from '@client:common/form';
import { Icon } from '@client:common/icon';
import { Model } from '@client:common/Model';
import { Dialog } from '@client:common/overlay/dialog';
import { Template, TemplateLayer, TemplateLayerChildren } from '@client:common/Template';
import { Tool, ToolLayer } from '@client:common/Tool';

import './ModelComparisonTable.light.scss';
import './ModelComparisonTable.dark.scss';

interface Props {
  models: Model[];
  selectedModels: Model[];
  rowSpecifications: Array<{ path: string; label?: string; level?: number }>;
  template: Template;
  tools: { [toolName: string]: Tool };
  className?: string;
  children?: ReactChild;
  scoreData?: { label: string; scores: Array<{ modelId: string; score: string | number }> };
  onSelectedModelsChange?: (selectedModels: Model[]) => void;
}

const COMPACT_VIEW = true;

export function ModelComparisonTable(props: Props) {
  const [selectedModels, setSelectedModels] = useState(props.selectedModels);
  useEffect(() => {
    setSelectedModels(props.selectedModels);
  }, [props.selectedModels]);

  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const columnsPerPage = 2;

  const updateIsMobile = () => {
    const screenWidth = window.innerWidth;
    const updatedIsMobile = screenWidth < 640;
    setIsMobile(updatedIsMobile);
    if (!updatedIsMobile) {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  const totalPages = isMobile ? Math.ceil(selectedModels.length / columnsPerPage) : 1;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const showModelSelectionDialog = () => {
    return new Promise<Model[]>((resolve) => {
      const modelSelectionFormControl = new FormControl(selectedModels);
      const dialogRef = Dialog.create(
        <>
          <div className="top-action-bar">
            <IconButton
              style="basic"
              color="primary"
              onClick={() => dialogRef.close()}
              className="close"
            >
              <Icon icon="close" />
            </IconButton>
          </div>
          <CheckboxGroup
            label="Compare models"
            hint="Edit selected models"
            formControl={modelSelectionFormControl}
          >
            {props.models.map((model) => (
              <Checkbox
                key={model.name}
                label={model.name}
                name="model-comparison-table"
                value={model}
              />
            ))}
          </CheckboxGroup>
        </>
      )
        .addButton(
          <Button
            style="basic"
            color="primary"
            onClick={() => dialogRef.close()}
          >
            <Ripple />
            CANCEL
          </Button>
        )
        .addButton(
          <Button
            style="raised"
            color="primary"
            onClick={() => {
              dialogRef.close();
              resolve(modelSelectionFormControl.getValue());
            }}
          >
            <Ripple />
            UPDATE
          </Button>
        )
        .open();
    });
  };

  const onSelectModels = () => {
    showModelSelectionDialog().then((payload) => {
      setSelectedModels(payload);
      setCurrentPage(1);
      props.onSelectedModelsChange?.(payload);
    });
  };

  const onRemoveModelFromTable = (model: Model) => {
    const newSelectedModels = selectedModels.filter((e) => e !== model);
    setSelectedModels(newSelectedModels);
    const newTotalPages = Math.ceil(newSelectedModels.length / columnsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }
    props.onSelectedModelsChange?.(newSelectedModels);
  };

  const getVisibleModels = () => {
    if (!isMobile) {
      return selectedModels;
    }

    const startIndex = (currentPage - 1) * columnsPerPage;
    const endIndex = Math.min(startIndex + columnsPerPage, selectedModels.length);
    return selectedModels.slice(startIndex, endIndex);
  };

  const visibleModels = getVisibleModels();

  const renderLayerRows = () => {
    if (selectedModels.length === 0) {
      return null;
    }

    const visitedLayerMap = new Map<string, number>();

    const rowsData = props.rowSpecifications.flatMap((spec) => {
      const rows = [] as Array<{
        key: string;
        header: boolean;
        level: number;
        label: string;
        path: string;
        toolValues: (boolean | null)[];
        className: string;
      }>;

      const layerNames = spec.path.split('/');
      let templateLayer: TemplateLayer | TemplateLayerChildren = null;
      let toolLayers: (ToolLayer | boolean)[] = null;
      let currentSubsectionLevel = 0;

      for (const layerName of layerNames) {
        if (templateLayer === null) {
          templateLayer = props.template[layerName];
          toolLayers = selectedModels.map((model) => props.tools[model.acronym][layerName] || false);
          if (visitedLayerMap.has(layerName)) {
            continue;
          }
          visitedLayerMap.set(layerName, 0);
          if (!spec.label) {
            rows.push({
              key: spec.path + layerName,
              header: true,
              level: 0,
              label: templateLayer.label,
              path: spec.path,
              toolValues: selectedModels.map(() => false),
              className: 'model-comparison-table__section'
            });
          }
        } else if ('children' in templateLayer && 'children' in (templateLayer as TemplateLayer).children[layerName]) {
          templateLayer = (templateLayer as TemplateLayer).children[layerName];
          toolLayers = toolLayers.map((layer) => typeof layer === 'boolean' ? false : layer?.[layerName] || false);
          if (visitedLayerMap.has(layerName)) {
            currentSubsectionLevel = visitedLayerMap.get(layerName);
            continue;
          }
          currentSubsectionLevel++;
          if ('level' in spec) {
            currentSubsectionLevel = spec.level;
          }
          visitedLayerMap.set(layerName, currentSubsectionLevel);
          if (!spec.label || layerName === layerNames[layerNames.length - 1]) {
            rows.push({
              key: spec.path + layerName,
              header: true,
              level: currentSubsectionLevel,
              label: spec.label || templateLayer.label,
              path: spec.path,
              toolValues: selectedModels.map((): null => null),
              className: `model-comparison-table__subsection level-${currentSubsectionLevel}`
            });
          }
        } else {
          if (layerName in templateLayer) {
            templateLayer = templateLayer[layerName as keyof (TemplateLayer | TemplateLayerChildren)] as (TemplateLayer | TemplateLayerChildren);
          } else {
            templateLayer = (templateLayer as TemplateLayer).children[layerName];
            toolLayers = toolLayers.map((layer) => typeof layer === 'boolean' ? false : layer?.[layerName] || false);
          }
          rows.push({
            key: spec.path + layerName,
            header: false,
            level: spec.level || currentSubsectionLevel,
            label: spec.label || templateLayer.label,
            path: spec.path,
            toolValues: toolLayers.map((layer) => typeof layer === 'boolean' ? layer : null),
            className: currentSubsectionLevel === 0 || spec.level === 0
              ? 'model-comparison-table__section-content-row'
              : `model-comparison-table__subsection-content-row level-${spec.level || currentSubsectionLevel}`
          });
        }
      }

      return rows;
    });

    if (COMPACT_VIEW) {
      const parentSectionMap = new Map<string, string>();
      rowsData.forEach(row => {
        if (row.header && row.level === 0) {
          const topLevelSection = row.path.split('/')[0];
          parentSectionMap.set(topLevelSection, row.label);
        }
      });

      const groupedRows = [] as Array<{
        key: string;
        header: boolean;
        level: number;
        label: string;
        path: string;
        toolValues: (boolean | null)[];
        className: string;
      }>;

      const sectionGroups = new Map<string, {
        headers: Array<typeof rowsData[0]>;
        level0Content: Array<typeof rowsData[0]>;
        subsections: Map<string, {
          header: typeof rowsData[0];
          content: Array<typeof rowsData[0]>;
        }>;
      }>();

      rowsData.forEach(row => {
        const pathComponents = row.path.split('/');
        const topLevelSection = pathComponents[0];

        if (!sectionGroups.has(topLevelSection)) {
          sectionGroups.set(topLevelSection, {
            headers: [],
            level0Content: [],
            subsections: new Map()
          });
        }

        const sectionGroup = sectionGroups.get(topLevelSection);

        if (row.level === 0) {
          if (row.header) {
            sectionGroup.headers.push(row);
          } else {
            sectionGroup.level0Content.push(row);
          }
        } else if (row.level > 0) {
          const subsectionId = pathComponents.length > 1 ? pathComponents[1] : '';

          if (row.header) {
            if (!sectionGroup.subsections.has(subsectionId)) {
              sectionGroup.subsections.set(subsectionId, {
                header: row,
                content: []
              });
            } else {
              sectionGroup.subsections.get(subsectionId).header = row;
            }
          } else {
            if (!sectionGroup.subsections.has(subsectionId)) {
              sectionGroup.subsections.set(subsectionId, {
                header: null,
                content: []
              });
            }

            sectionGroup.subsections.get(subsectionId).content.push(row);
          }
        }
      });

      sectionGroups.forEach((sectionGroup, topLevelSection) => {
        const parentLabel = parentSectionMap.get(topLevelSection);

        sectionGroup.subsections.forEach((subsection) => {
          if (subsection.header && parentLabel) {
            groupedRows.push({
              ...subsection.header,
              level: 0,
              label: `${parentLabel} - ${subsection.header.label}`,
              className: 'model-comparison-table__section'
            });
          }

          subsection.content.forEach(row => {
            groupedRows.push({
              ...row,
              level: 0,
              className: 'model-comparison-table__section-content-row'
            });
          });
        });

        if (parentLabel && sectionGroup.subsections.size > 0 && sectionGroup.level0Content.length > 0) {
          groupedRows.push({
            key: `${topLevelSection}-other`,
            header: true,
            level: 0,
            label: `${parentLabel} - Other`,
            path: topLevelSection,
            toolValues: sectionGroup.headers.length > 0
              ? sectionGroup.headers[0].toolValues
              : Array(selectedModels.length).fill(null),
            className: 'model-comparison-table__section'
          });
        }

        if (sectionGroup.level0Content.length > 0) {
          if (sectionGroup.subsections.size === 0 && sectionGroup.headers.length > 0) {
            groupedRows.push({
              ...sectionGroup.headers[0],
              className: 'model-comparison-table__section'
            });
          }

          sectionGroup.level0Content.forEach(row => {
            groupedRows.push({
              ...row,
              className: 'model-comparison-table__section-content-row'
            });
          });
        }
      });

      const sections: Array<{
        header: typeof rowsData[0];
        content: Array<typeof rowsData[0]>;
      }> = [];

      let currentSection: {
        header: typeof rowsData[0];
        content: Array<typeof rowsData[0]>;
      } | null = null;

      groupedRows.forEach((row) => {
        if (row.header) {
          if (currentSection) {
            sections.push({ ...currentSection });
          }
          currentSection = {
            header: row,
            content: []
          };
        } else if (currentSection) {
          currentSection.content.push(row);
        }
      });

      if (currentSection && currentSection.content.length > 0) {
        sections.push(currentSection);
      }

      return (
        <>
          {sections.map((section) => (
            <MobileTableSection
              key={section.header.key}
              header={section.header}
              allModels={selectedModels}
              currentPage={currentPage}
              columnsPerPage={columnsPerPage}
              visibleColumnCount={isMobile ? Math.min(columnsPerPage, visibleModels.length) : visibleModels.length}
            >
              {section.content.map((row) => (
                <tr
                  key={row.key}
                  className={row.className}>
                  <td key={row.label}>{row.label}</td>
                  {isMobile
                    ? row.toolValues
                      .slice((currentPage - 1) * columnsPerPage, currentPage * columnsPerPage)
                      .map((value, i) => {
                        const modelIndex = (currentPage - 1) * columnsPerPage + i;
                        if (modelIndex >= selectedModels.length) {
                          return null;
                        }
                        if (value === null) {
                          return <td key={visibleModels[i].acronym} />;
                        }
                        if (value === true) {
                          return (
                            <td
                              key={visibleModels[i].acronym}
                              className='highlighted'>
                              <Icon icon='check' />
                            </td>
                          );
                        }
                        return <td key={visibleModels[i].acronym} />;
                      })
                    : row.toolValues.map((value, i) => {
                      if (value === null) {
                        return <td key={selectedModels[i].acronym} />;
                      }
                      if (value === true) {
                        return (
                          <td
                            key={selectedModels[i].acronym}
                            className='highlighted'>
                            <Icon icon='check' />
                          </td>
                        );
                      }
                      return <td key={selectedModels[i].acronym} />;
                    })
                  }
                </tr>
              ))}
            </MobileTableSection>
          ))}
        </>
      );
    }

    return rowsData.map((row) => (
      <tr
        key={row.key}
        className={row.className}>
        <td key={row.label}>{row.label}</td>
        {isMobile
          ? row.toolValues
            .slice((currentPage - 1) * columnsPerPage, currentPage * columnsPerPage)
            .map((value, i) => {
              const modelIndex = (currentPage - 1) * columnsPerPage + i;
              if (modelIndex >= selectedModels.length) {
                return null;
              }
              if (value === null) {
                return <td key={visibleModels[i].acronym} />;
              }
              if (value === true) {
                return (
                  <td
                    key={visibleModels[i].acronym}
                    className='highlighted'>
                    <Icon icon='check' />
                  </td>
                );
              }
              return <td key={visibleModels[i].acronym} />;
            })
          : row.toolValues.map((value, i) => {
            if (value === null) {
              return <td key={selectedModels[i].acronym} />;
            }
            if (value === true) {
              return (
                <td
                  key={selectedModels[i].acronym}
                  className='highlighted'>
                  <Icon icon='check' />
                </td>
              );
            }
            return <td key={selectedModels[i].acronym} />;
          })
        }
      </tr>
    ));
  };

  const renderScoreRow = () => {
    if (!props.scoreData || selectedModels.length === 0) {
      return null;
    }

    const visibleScores = isMobile
      ? props.scoreData.scores
        .filter((score, index) => {
          const startIndex = (currentPage - 1) * columnsPerPage;
          const endIndex = startIndex + columnsPerPage;
          return index >= startIndex && index < endIndex;
        })
      : props.scoreData.scores;

    return (
      <tr className='model-comparison-table__section-content-row score-row'>
        <td key='score-label'>{props.scoreData.label}</td>
        {isMobile
          ? visibleScores.map(score => {
            return <td key={`score-${score.modelId}`}>{score.score}</td>;
          })
          : props.scoreData.scores.map(score => {
            return <td key={`score-${score.modelId}`}>{score.score}</td>;
          })
        }
      </tr>
    );
  };

  return (
    <div className={`model-comparison-table${props.className ? ' ' + props.className : ''}`}>
      <div
        className={`model-comparison-table__table-wrapper${isMobile ? ' mobile-view' : ''}`}
        style={{ overflowX: 'auto' }}>
        <table className={`model-comparison-table__table${isMobile ? ' mobile-table' : ''}`}>
          <thead>
            {isMobile && selectedModels.length > 0 && (
              <tr className="model-comparison-table__controls-row">
                <th>
                  <IconButton
                    style='stroked'
                    color='primary'
                    onClick={onSelectModels}
                    className="model-comparison-table__customize-button"
                  >
                    <Ripple />
                    <Icon icon='edit' />
                    CUSTOMIZE
                  </IconButton>
                </th>
                <th
                  colSpan={columnsPerPage}
                  className="model-comparison-table__pagination-cell">
                  <div className="model-comparison-table__pagination">
                    <IconButton
                      style="basic"
                      color="primary"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      <Ripple />
                      <Icon icon="chevron_left" />
                    </IconButton>
                    <span className="model-comparison-table__pagination-text">
                      {currentPage} OF {totalPages}
                    </span>
                    <IconButton
                      style="basic"
                      color="primary"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      <Ripple />
                      <Icon icon="chevron_right" />
                    </IconButton>
                  </div>
                </th>
              </tr>
            )}
            <tr className='model-comparison-table__table-heading-row'>
              <th>
                {!isMobile && (selectedModels.length < props.models.length) ? (
                  <IconButton
                    style='stroked'
                    color='primary'
                    onClick={onSelectModels}>
                    <Ripple />
                    <Icon icon='add' />
                    ADD MODEL
                  </IconButton>
                ) : null}
              </th>
              {visibleModels.map((model) => (
                <th
                  key={model.acronym}
                  className='model-comparison-table__model-name'
                  style={{
                    width: `calc((100% - 25%) / ${visibleModels.length})`
                  }}
                >
                  {model.acronym}
                  {!isMobile && (
                    <IconButton
                      className='model-comparison-table__remove-model'
                      style='basic'
                      color='primary'
                      onClick={() => onRemoveModelFromTable(model)}
                    >
                      <Ripple
                        fixed
                        duration={1000} />
                      <Icon icon='close' />
                    </IconButton>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderScoreRow()}
            {props.children}
            {renderLayerRows()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface MobileTableSectionProps {
  header: {
    key: string;
    header: boolean;
    level: number;
    label: string;
    path: string;
    toolValues: (boolean | null)[];
    className: string;
  };
  allModels: Model[];
  currentPage: number;
  columnsPerPage: number;
  visibleColumnCount: number;
  children: ReactNode;
}

function MobileTableSection({
  header,
  allModels,
  currentPage,
  columnsPerPage,
  visibleColumnCount,
  children
}: MobileTableSectionProps) {
  const [expanded, setExpanded] = useState(true);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <tr
        key={header.key}
        className={`${header.className} mobile-section-header`}
        onClick={toggleExpanded}
      >
        <td
          key={header.label}
          className="section-title-cell"
        >
          <Icon
            icon={expanded ? 'arrow_drop_down' : 'arrow_right'}
            size='24px'
          />
          {header.label}
        </td>
        {Array.from({ length: visibleColumnCount }).map((_, i) => {
          const modelIndex = (currentPage - 1) * columnsPerPage + i;
          if (modelIndex >= allModels.length) {
            return <td key={`empty-${i}`} />;
          }

          const value = header.toolValues[(currentPage - 1) * columnsPerPage + i];
          if (value === null) {
            return <td key={`model-${i}`} />;
          }
          if (value === true) {
            return (
              <td
                key={`model-${i}`}
                className='highlighted'>
                <Icon icon='check' />
              </td>
            );
          }
          return <td key={`model-${i}`} />;
        })}
      </tr>
      {expanded && children}
    </>
  );
}
